/* eslint-disable max-depth */

/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useRef, useState } from 'react';
import isPromise from 'is-promise';

const noop = () => undefined;

const alwaysTrue = () => true;

export const consumeGenerator = async (generator, options) => {
  const {
    isExecutable = alwaysTrue,
    onIterationValue = noop,
    onStart = noop,
    onDone = noop,
    onError = noop
  } = options;
  const tasks = [];
  let next = generator.next();
  const currentTaskId = onStart();

  const canContinue = () => !next.done && isExecutable(currentTaskId);

  const handleError = e => {
    onError(e);
    tasks.forEach(task => task && task.onError && task.onError(e));
  };

  const onUnhandledError = e => {
    onError(e);
    tasks.forEach(task => task && task.onUnhandledError && task.onUnhandledError(e));
  };

  const handleDone = v => {
    onDone(v);
    tasks.forEach(task => task && task.onDone && task.onDone(v));
  };

  try {
    while (canContinue()) {
      const yieldValue = next.value;

      if (isPromise(yieldValue)) {
        try {
          const result = await yieldValue;
          (result === null || result === void 0 ? void 0 : result.unionGenerator) && tasks.push(result);

          if (canContinue()) {
            onIterationValue(result);
            next = generator.next(result);
          }
        } catch (e) {
          handleError(e);
          next = generator.throw(e);
        }
      } else {
        (yieldValue === null || yieldValue === void 0 ? void 0 : yieldValue.unionGenerator) && tasks.push(yieldValue);
        onIterationValue(yieldValue);
        next = generator.next(yieldValue);
      }
    }
  } catch (e) {
    // eslint-disable-next-line
    console.log(e);
    onUnhandledError(e);
  }

  if (isExecutable(currentTaskId) && next.done) {
    handleDone(next.value);
    return next.value;
  }
};
export const useLatestGeneratorCallback = (generatorFunction, deps) => {
  const lastetTaskId = useRef(1);
  const [done, setDone] = useState(true);
  const [iterationValue, onIterationValue] = useState();
  const [canceledValue, setCanceledValue] = useState(false);
  const [error, setError] = useState();
  const [running, setRunning] = useState(false);
  const canceled = useRef(false);
  const returnValue = useCallback(async (...args) => {
    const generator = generatorFunction(...args);
    const result = await consumeGenerator(generator, {
      isExecutable: currentTaskId => {
        return !canceled.current && currentTaskId === lastetTaskId.current;
      },
      onStart: () => {
        canceled.current = false;
        setCanceledValue(false);
        setDone(false);
        setError('');
        setRunning(true);
        return ++lastetTaskId.current;
      },
      onDone: () => {
        setRunning(false);
        setDone(true);
      },
      onIterationValue,
      onError: e => {
        setError(e.message);
      }
    });
    return result;
  }, deps);
  returnValue.done = done;
  returnValue.value = iterationValue;
  returnValue.canceled = canceledValue;
  returnValue.error = error;
  returnValue.running = running;
  returnValue.cancel = useCallback(() => {
    canceled.current = true;
    setRunning(false);
  }, []);
  return returnValue;
};
export const useGeneratorCallback = (generatorFunction, deps) => {
  const [done, setDone] = useState(true);
  const [iterationValue, onIterationValue] = useState();
  const [canceledValue, setCanceledValue] = useState(false);
  const [error, setError] = useState();
  const [running, setRunning] = useState(false);
  const canceled = useRef(false);
  const returnValue = useCallback(async (...args) => {
    const generator = generatorFunction(...args);
    const result = await consumeGenerator(generator, {
      isExecutable: () => {
        return !canceled.current;
      },
      onStart: () => {
        canceled.current = false;
        setCanceledValue(false);
        setDone(false);
        setError('');
        setRunning(true);
        return 1;
      },
      onDone: () => {
        setRunning(false);
        setDone(true);
      },
      onIterationValue,
      onError: e => {
        setError(e.message);
      }
    });
    return result;
  }, deps);
  returnValue.done = done;
  returnValue.value = iterationValue;
  returnValue.canceled = canceledValue;
  returnValue.error = error;
  returnValue.running = running;
  returnValue.cancel = useCallback(() => {
    canceled.current = true;
    setRunning(false);
  }, []);
  return returnValue;
};
export const useGeneratorEffect = (generatorFunction, deps) => {
  useEffect(() => {
    let cleanup = noop;
    const generator = generatorFunction();
    consumeGenerator(generator, {
      onDone: returnValue => {
        cleanup = returnValue;
      }
    });
    return cleanup;
  }, deps);
};
export const useLatestGeneratorEffect = (generatorFunction, deps) => {
  const lastetTaskId = useRef(1);
  useEffect(() => {
    const generator = generatorFunction();
    let cleanup = noop;
    consumeGenerator(generator, {
      isExecutable: currentTaskId => {
        return currentTaskId === lastetTaskId.current;
      },
      onStart: () => {
        return ++lastetTaskId.current;
      },
      onDone: returnValue => {
        cleanup = returnValue;
      }
    });
    return cleanup;
  }, deps);
};
//# sourceMappingURL=es/generator.js.map